import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireOwner?: boolean;
  requireEmployee?: boolean;
  customerOnly?: boolean;
}

export const ProtectedRoute = ({ children, requireAdmin = false, requireOwner = false, requireEmployee = false, customerOnly = false }: ProtectedRouteProps) => {
  const { user, loading, isAdmin, adminRequires2FA, isOwner, isEmployee, checkingRole, isSigningOut, initialAuthDone } = useAuth();
  const location = useLocation();

  if (loading || (!initialAuthDone && checkingRole) || (requireAdmin && checkingRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // If user is signing out, redirect directly to home
    if (isSigningOut) {
      return <Navigate to="/" replace />;
    }
    
    // Build returnTo parameter with current location
    const returnTo = `${location.pathname}${location.search}`;
    const encodedReturnTo = encodeURIComponent(returnTo);
    
    // Redirect to appropriate auth page based on route protection level
    if (requireAdmin) {
      return <Navigate to={`/auth/admin?returnTo=${encodedReturnTo}`} replace />;
    }
    if (requireOwner) {
      return <Navigate to={`/auth/owner?returnTo=${encodedReturnTo}`} replace />;
    }
    return <Navigate to={`/auth/client?returnTo=${encodedReturnTo}`} replace />;
  }

  if (requireAdmin && !isAdmin) {
    if (adminRequires2FA) {
      const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
      return <Navigate to={`/auth/admin?returnTo=${returnTo}`} replace />;
    }
    // If user is owner/employee, send them to owner dashboard, otherwise to home
    if (isOwner || isEmployee) {
      return <Navigate to="/owner/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (requireOwner && !isOwner && !isEmployee) {
    // If user is admin, send them to admin dashboard, otherwise to home
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (requireEmployee && !isEmployee && !isOwner && !isAdmin) {
    // Only employees, owners, and admins can access employee-protected routes
    return <Navigate to="/" replace />;
  }

  if (customerOnly && (isAdmin || isOwner || isEmployee)) {
    // Redirect admins, owners, and employees to their respective dashboards
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    if (isOwner || isEmployee) {
      return <Navigate to="/owner/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};